import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Target, Plus, Trash2, TriangleAlert, CheckCircle2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const CATEGORY_LABELS = {
  nomina: "Nómina", hipoteca: "Hipoteca", alquiler: "Alquiler", servicios: "Servicios",
  supermercado: "Supermercado", restaurantes: "Restaurantes", transporte: "Transporte",
  gasolina: "Gasolina", salud: "Salud", ocio: "Ocio", viajes: "Viajes", compras: "Compras",
  educacion: "Educación", impuestos: "Impuestos", transferencias: "Transferencias",
  ingresos: "Otros ingresos", cripto: "Cripto", inversiones: "Inversiones", otros: "Otros",
};

const EXPENSE_CATEGORIES = Object.keys(CATEGORY_LABELS).filter(
  (k) => !["nomina", "ingresos"].includes(k)
);

// Shared status helpers to keep the visual "over / warn / ok" mapping in a
// single place instead of nested ternaries scattered across every row.
const budgetStatus = (pct) => {
  if (pct >= 100) return "over";
  if (pct >= 80) return "warn";
  return "ok";
};
const BUDGET_BAR = { over: "#D95D39", warn: "#E58B6E", ok: "#1C7C54" };
const BUDGET_TEXT = { over: "text-[#D95D39]", warn: "text-[#E58B6E]", ok: "text-[#1C7C54]" };
const BUDGET_TEXT_MUTED = { over: "text-[#D95D39]", warn: "text-[#E58B6E]", ok: "text-[#5F7368]" };

function BudgetDialog({ open, onOpenChange, existing, onSaved }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("supermercado");
  const [amount, setAmount] = useState(200);
  const [currency, setCurrency] = useState(user?.base_currency || "USD");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCategory(existing?.category || "supermercado");
      setAmount(existing?.amount ?? 200);
      setCurrency(existing?.currency || user?.base_currency || "USD");
    }
  }, [open, existing, user]);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Importe inválido");
    setSaving(true);
    try {
      await api.post("/budgets", { category, amount: Number(amount), currency });
      toast.success("Presupuesto guardado");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {existing ? "Editar presupuesto" : "Nuevo presupuesto"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div>
            <label className="text-sm font-medium text-[#0A110E]">Categoría</label>
            <Select value={category} onValueChange={setCategory} disabled={!!existing}>
              <SelectTrigger data-testid="budget-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map((k) => (
                  <SelectItem key={k} value={k}>{CATEGORY_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A110E]">Tope mensual ({currency})</label>
            <Input
              data-testid="budget-amount"
              type="number"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="budget-save-btn" onClick={submit} disabled={saving} className="bg-[#0F291E] hover:bg-[#1C7C54]">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Budgets() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/budgets");
      setItems(r.data);
    } catch (e) { logger.error("budgets load", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (cat) => {
    try {
      await api.delete(`/budgets/${cat}`);
      toast.success("Presupuesto eliminado");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Target className="w-3 h-3" /> {t("budgets.kicker")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{t("budgets.title")}</h1>
          <p className="text-[#5F7368] mt-2">{t("budgets.subtitle", { defaultValue: "Un tope mensual por categoría. La app te avisa cuando te acercas." })}</p>
        </div>
        <Button data-testid="add-budget-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("common.add")}
        </Button>
      </div>

      <div className="card-surface p-6">
        {loading ? (
          <div className="text-[#5F7368]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-[#8A9992] mb-4">Aún no hay presupuestos.</p>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#0F291E] hover:bg-[#1C7C54]">
              <Plus className="w-4 h-4 mr-2" /> Añade el primero
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-5" data-testid="budgets-list">
            {items.map((b) => {
              const pct = Math.min(100, b.progress_pct || 0);
              const status = budgetStatus(pct);
              const barColor = BUDGET_BAR[status];
              const over = status === "over";
              return (
                <div key={b.category} data-testid={`budget-${b.category}`} className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center">
                      {over ? <TriangleAlert className="w-4 h-4 text-[#D95D39]" /> : <CheckCircle2 className="w-4 h-4 text-[#1C7C54]" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#0A110E]">{CATEGORY_LABELS[b.category] || b.category}</div>
                      <div className="text-xs text-[#8A9992] tabular-nums">
                        {formatCurrency(b.used || 0, b.currency)} de {formatCurrency(b.amount, b.currency)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`text-sm font-semibold tabular-nums ${BUDGET_TEXT[status]}`}>
                        {pct.toFixed(0)}%
                      </div>
                      <button
                        onClick={() => { setEditing(b); setOpen(true); }}
                        data-testid={`edit-budget-${b.category}`}
                        className="text-xs text-[#5F7368] underline hover:text-[#0A110E]"
                      >
                        editar
                      </button>
                      <button
                        onClick={() => remove(b.category)}
                        data-testid={`delete-budget-${b.category}`}
                        className="p-1 rounded hover:bg-[#EBEBEA]"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-[#D95D39]" />
                      </button>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-[#EBEBEA] overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: barColor }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BudgetDialog open={open} onOpenChange={setOpen} existing={editing} onSaved={load} />
    </div>
  );
}

/**
 * Compact budget card used on Dashboard: shows a mini overview.
 */
export function BudgetsCard() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/budgets").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items === null) return null;

  if (items.length === 0) {
    return (
      <div className="card-surface p-6" data-testid="budgets-card">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-[#1C7C54]" />
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Presupuestos</p>
        </div>
        <p className="text-sm text-[#5F7368]">
          Fija un tope mensual por categoría y verás aquí cuánto te queda.
        </p>
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="budgets-card">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-4 h-4 text-[#1C7C54]" />
        <div>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Presupuestos</p>
          <h3 className="font-display text-lg font-semibold text-[#0A110E]">Este mes</h3>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {items.slice(0, 5).map((b) => {
          const pct = Math.min(100, b.progress_pct || 0);
          const status = budgetStatus(pct);
          const barColor = BUDGET_BAR[status];
          return (
            <div key={b.category}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-[#0A110E]">{CATEGORY_LABELS[b.category] || b.category}</span>
                <span className={`tabular-nums font-semibold ${BUDGET_TEXT_MUTED[status]}`}>
                  {formatCurrency(b.used || 0, b.currency)} / {formatCurrency(b.amount, b.currency)}
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-[#EBEBEA] mt-1 overflow-hidden">
                <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: barColor }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
